import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { IMeterReadingPhotoItem, PhotoSource } from "./meterPhotos.types"
import { GetMeterReadingPhotoEvidenceResponse } from "@/api/backofficeRest/generated/model"
import dayjs from "dayjs"
import { formatDate } from "@/helpers/dateHelper"
import { IMeterAppointment } from "@/models/meterAppointment/meterAppointment.types"

export type PhotoEvidenceResponseRaw = GetMeterReadingPhotoEvidenceResponse

export interface PhotoUrlResponse {
  url: string
}

export const PhotoEvidenceParser: IParser<
  IMeterReadingPhotoItem[],
  PhotoEvidenceResponseRaw
> = {
  toDomain: (data) => {
    const getImageObject = (date, imageId, index) => {
      return {
        displayName:
          dayjs(date).format("DD-MMM-YYYY") + "-image-" + (index + 1),
        path: imageId,
      }
    }

    try {
      return (data.groupedEvidences ?? [])
        .filter((tableRow) => tableRow.meterReadingPhotoEvidences?.length)
        .map((tableRow) => {
          const evidences = tableRow.meterReadingPhotoEvidences ?? []
          const [first] = evidences
          const meterReads: number[] = []
          if (first?.standardRead) meterReads.push(first.standardRead)
          if (first?.dayRead) meterReads.push(first.dayRead)
          if (first?.nightRead) meterReads.push(first.nightRead)

          const createdDate = dayjs(first?.createdAt)
          return {
            date: formatDate(createdDate),
            sortDate: createdDate.toISOString(),
            fuel: first?.fuel ?? "",
            meterPoint: first?.meterPointIdentifier ?? "",
            failedRead: meterReads,
            errorCode: first?.errorCode ?? "",
            requestReason: first?.requestReason ?? "",
            freshdesk: String(tableRow.freshdeskTicketId ?? ""),
            source: PhotoSource.Customer,
            viewImage: evidences.map((item, index) =>
              getImageObject(first?.createdAt, item.imageIdentifier, index),
            ),
          }
        })
    } catch (error) {
      throw new ParseError(error)
    }
  },
}

function getFuelType(mpan: string, mprn: string): string {
  if (mpan && mprn) return "Dual"
  if (mpan) return "Elec"
  if (mprn) return "Gas"
  return ""
}

function parseAbortReason(abortReason: string): string {
  try {
    const entries = JSON.parse(abortReason)
    const reason = entries.find(
      (e: { question: string }) => e.question === "Abort reason",
    )
    return reason?.answer || ""
  } catch {
    return abortReason
  }
}

function getErrorCode(abortReason?: string, cancelReason?: string): string {
  if (abortReason) {
    const parsed = parseAbortReason(abortReason)
    if (parsed) return `Aborted appointment: ${parsed}`
    return ""
  }
  if (cancelReason) return `Cancelled appointment: ${cancelReason}`
  return ""
}

// Highlight applied to rows belonging to another account at the same site -
// the theme's `highlight` token (SoDataTable renders this via
// internalItem.raw.rowColor).
export const OTHER_ACCOUNT_ROW_COLOR = "rgb(var(--v-theme-highlight))"

// Stamps each photo item with the account it belongs to. Rows from another
// account at the site carry a label ('Newer account' / 'Previous account') and
// get a rowColor so the table highlights them.
export function tagPhotoItemsForAccount(
  items: IMeterReadingPhotoItem[],
  {
    accountNumber,
    otherAccountLabel,
  }: {
    accountNumber: string
    otherAccountLabel?: string
  },
): IMeterReadingPhotoItem[] {
  return items.map((item) => ({
    ...item,
    accountNumber,
    ...(otherAccountLabel
      ? { otherAccountLabel, rowColor: OTHER_ACCOUNT_ROW_COLOR }
      : {}),
  }))
}

export function appointmentsToPhotoItems(
  appointments: IMeterAppointment[],
): IMeterReadingPhotoItem[] {
  return appointments
    .filter(
      (appointment) =>
        appointment.survey?.some((s) => s.imageUrl?.length) ?? false,
    )
    .map((appointment) => {
      const surveyImages = appointment.survey!.filter((s) => s.imageUrl?.length)
      const meterPoints = [appointment.mpan, appointment.mprn].filter(Boolean)
      const fuel = getFuelType(appointment.mpan, appointment.mprn)
      const jobNumber = appointment.displayJobNumber || ""
      const apptType = appointment.appointmentType || ""
      const requestReason =
        jobNumber && apptType
          ? `${jobNumber} - ${apptType}`
          : jobNumber || apptType
      const errorCode = getErrorCode(
        appointment.abortReason,
        appointment.cancelReason,
      )

      const slotDate = dayjs(appointment.slotStartDateTime)
      return {
        date: formatDate(slotDate),
        sortDate: slotDate.toISOString(),
        fuel,
        meterPoint: meterPoints.join("; "),
        failedRead: "N/A",
        errorCode,
        requestReason,
        freshdesk: "",
        source: PhotoSource.SiteVisit,
        installer: appointment.installer,
        viewImage: surveyImages.map((survey, index) => ({
          displayName: survey.question || `Site visit image ${index + 1}`,
          path: "",
          directUrl: survey.imageUrl,
        })),
      }
    })
}
